"use client";

import { FormsIds, OrderSource, PaymentMethods, ScanDocumentType } from "../../Utils/Constants";

import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Paper,
  Box,
} from "@mui/material";
import ProductPanel from "./ProductPanel";
import { Controller, useForm } from "react-hook-form";
import {
  type OrderModel,
  OrderValidationSchema,
  type ProductsModel,
  type UserModel,
} from "../../Utils/Models";
import { yupResolver } from "@hookform/resolvers/yup";
import { getData } from "../../Services/getData";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import type { AxiosResponse } from "axios";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { Vocabulary } from "../../Utils/Vocabulary";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import { postData } from "../../Services/postData";
import { useNavigate } from "react-router-dom";
import appendFormData, { onFormError, isValidDate, removeDiacritics } from "../../Utils/Utils";
import ScanId from "../ScanIdModal";
import { blobToFile } from "../../Utils/ProcessImages";
import withRole from "../../Hooks/withRole";
import { toast } from "react-toastify";

function OrderForm(props: any) {
  const [products, setProducts] = useState([] as ProductsModel[]);
  const [users, setUsers] = useState([] as UserModel[]);
  const memo = useCustomMemo();
  const managements = memo?.managements || [];
  const navigate = useNavigate();
  const ref: any = useRef();
  const [scanIdModal, setScanIdModal] = useState(false);
  const [scanDriverLicenseModal, setScanDriverLicenseModal] = useState(false);
  const [drivingLicenseScanned, setDrivingLicenseScanned] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    reset,
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useForm({
    resolver: yupResolver(OrderValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  // Watch for changes to the form to detect workPoint changes
  const formValues = watch();

  /**
   * When workPoint changes, filter users by that workPointId and clear the recommendation user
   */
  useEffect(() => {
    const workPointId = (formValues as any)?.workPoint;
    if (workPointId) {
      getUsers(workPointId);
      // Clear the recommendation user when work point changes
      setValue("recommendation_id.user_id", null);
    }
  }, [(formValues as any)?.workPoint]);

  /**
   * Set default payment method to Cash for recommendations
   */
  useEffect(() => {
    setValue("recommendation_id.paymentMethod", PaymentMethods.CASH);
  }, [setValue]);

  /**
   *
   */
  useEffect(() => {
    getProducts();
    getUsers();
    // addNewProductForList();
  }, []);

  /**
   *
   * @param search
   */
  const getProducts = () => {
    getData(`${endpoints.product.main}/paginated?page=0&perPage=100000`).then(
      (res: AxiosResponse) => {
        if (!res?.data) return;
        setProducts(res.data);
      }
    );
  };

  /**
   * Gets users for recommendation purposes with filters.
   * The list of users is used to populate the recommender field in the order form.
   * Filters applied:
   * - Optional workPointId filter
   * - Role name filter for "Functionar" users only
   * @param workPointId - Optional work point ID to filter users
   */
  const getUsers = (workPointId?: string) => {
    //get users for recommendation with filters
    const params = new URLSearchParams();

    if (workPointId) {
      params.append("workPointId", workPointId);
    }

    // Filter for Functionar role only
    params.append("roleName", "Functionar");

    const url = params.toString()
      ? `${endpoints.user.main}?${params.toString()}`
      : `${endpoints.user.main}`;

    getData(url).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setUsers(res.data);
    });
  };

  /**
   *
   * @param data
   */
  const saveOrder = (data: OrderModel) => {
    const ciFile = data?.client_id?.ci;
    const drivingLicenseFile = data?.client_id?.drivingLicense;
    if (ciFile instanceof File || drivingLicenseFile instanceof File) {
      const formData = new FormData();
      
      // Create a copy and remove File objects before appending
      const dataToSend = JSON.parse(JSON.stringify(data));
      if (dataToSend.client_id) {
        delete dataToSend.client_id.ci;
        delete dataToSend.client_id.drivingLicense;
      }
      
      appendFormData(formData, dataToSend);
      
      // Append the CI file
      if (ciFile instanceof File) {
        formData.append("ci", ciFile);
      }
      
      // Append the driving license file as array (drivingLicense[]) to match multer expectations
      if (drivingLicenseFile instanceof File) {
        formData.append("drivingLicense", drivingLicenseFile);
      }

      postData(endpoints.ordersEndpoint, formData)
        .then((res: AxiosResponse) => {
          if (res?.data) {
            navigate(`${localUrlEnum.order}/${res?.data?._id}`);
            setValue("_id", res?.data?._id);
            setValue("status", res?.data?.status);
          }
        })
        .catch((error) => {
          console.error("Error during API request:", error);
        });
    } else {
      postData(endpoints.ordersEndpoint, data)
        .then((res: AxiosResponse) => {
          if (res?.data) {
            navigate(`${localUrlEnum.order}/${res?.data?._id}`);
            setValue("_id", res?.data?._id);
            setValue("status", res?.data?.status);
          }
        })
        .catch((error) => {
          console.error("Error during API request:", error);
        });
    }
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    model = model ? model : getValues();
    saveOrder({ ...model, source: OrderSource.MANUAL });
  };

  /**
   * Processes the scanned ID data, closes the Scan ID modal,
   * and populates the order form with the extracted client details.
   *
   * @param data - The data object containing the scanned ID result.
   *   The result includes client information such as CNP, first name, last name,
   *   and address details (country, city, and county).
   */

  const processCICompleted = (data: any) => {
    const processedData = data.result;
    setScanIdModal(false);

    // Parse and validate dates from validity string
    let ciIssuedDate = "";
    let ciExpirationDate = "";
    if (processedData?.validity) {
      const dateParts = processedData.validity
        .split("-")
        .map((date: string) => date.trim());
      // Extract and validate issued date (first part)
      if (dateParts[0] && isValidDate(dateParts[0])) {
        ciIssuedDate = dateParts[0];
      }
      // Extract and validate expiry date (second part)
      if (dateParts[1] && isValidDate(dateParts[1])) {
        ciExpirationDate = dateParts[1];
      }
    }

    // Fill Data in from
    setValue("client_id.CNP", processedData?.CNP);
    setValue("client_id.firstName", processedData?.firstName);
    setValue("client_id.lastName", processedData?.lastName || processedData?.secondName);
    setValue("client_id.address.country", removeDiacritics(processedData?.addressCountry));
    setValue("client_id.address.city", removeDiacritics(processedData?.addressCity));
    setValue("client_id.address.county", removeDiacritics(processedData?.addressCounty));
    setValue("client_id.address.details", processedData?.addressMixed);
    // Additional fields from CI
    setValue("client_id.gender", processedData?.sex);
    setValue("client_id.ciSerialNumber", processedData?.series);
    setValue("client_id.ciNumber", processedData?.number);
    setValue("client_id.ciIssuedBy", processedData?.issuingAuthority);
    setValue("client_id.ciIssuedDate", ciIssuedDate);
    setValue("client_id.ciExpirationDate", ciExpirationDate);

    // Save driving categories if available (from driver's license)
    if (processedData?.categories && Array.isArray(processedData.categories)) {
      setValue("client_id.drivingCategories", processedData.categories);
    }

    // Collect all base64 images (front only for old IDs, front+back for new IDs)
    const imagePaths = (data?.image || [])
      .filter((img: any) => img?.path?.startsWith("data:"))
      .map((img: any) => img.path);

    if (imagePaths.length > 0) {
      mergeImages(imagePaths)
        .then((mergedImagePath) => {
          try {
            const file = blobToFile(mergedImagePath);
            setValue("client_id.ci", file);
          } catch (error) {
            console.error("Error converting CI image to file:", error);
          }
        })
        .catch((error) => {
          console.error("Error merging CI images:", error);
        });
    }
  };

  /**
   * Merges multiple images vertically into a single image.
   * @param imagePaths - Array of base64 image paths to merge
   * @returns Promise resolving to merged image as base64 string
   */
  const mergeImages = (imagePaths: string[]): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (imagePaths.length === 0) {
        reject(new Error("No images to merge"));
        return;
      }

      if (imagePaths.length === 1) {
        resolve(imagePaths[0]);
        return;
      }

      // Load all images
      const imageElements = imagePaths.map((path) => {
        const img = new Image();
        img.src = path;
        return img;
      });

      // Wait for all images to load
      Promise.all(
        imageElements.map(
          (img) =>
            new Promise<void>((resolveImg) => {
              img.onload = () => resolveImg();
            })
        )
      ).then(() => {
        // Calculate total height and max width for vertical stacking
        const totalHeight = imageElements.reduce((sum, img) => sum + img.height, 0);
        const maxWidth = Math.max(...imageElements.map((img) => img.width));

        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.width = maxWidth;
        canvas.height = totalHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        // Draw images vertically
        let currentY = 0;
        imageElements.forEach((img) => {
          ctx.drawImage(img, 0, currentY, img.width, img.height);
          currentY += img.height;
        });

        // Convert to base64
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      }).catch(reject);
    });
  };

  /**
   * Processes the scanned driving license data and stores relevant fields on the client.
   */
  const processDrivingLicenseCompleted = (data: any) => {
    try {
      const processedData = data.result;
      setScanDriverLicenseModal(false);

      if (!processedData) {
        toast.error("Permisul de conducere nu a putut fi procesat. Vă rugăm să încercați din nou.");
        return;
      }

      if (processedData?.categories && Array.isArray(processedData.categories)) {
        setValue("client_id.drivingCategories", processedData.categories);
      }

      // Extract and set driving license issue date (format: DD.MM.YYYY)
      if (processedData?.issueDate && isValidDate(processedData.issueDate)) {
        setValue("client_id.drivingLicenseIssuedDate", processedData.issueDate);
      }

      // Extract and set driving license expiration date (format: DD.MM.YYYY)
      if (processedData?.expiringDate && isValidDate(processedData.expiringDate)) {
        setValue("client_id.drivingLicenseExpirationDate", processedData.expiringDate);
      }

      // Get all base64 images
      const images = data?.image || [];
      const imagePaths = images
        .filter((img: any) => img?.path?.startsWith("data:"))
        .map((img: any) => img.path);

      if (imagePaths.length > 0) {
        try {
          // Merge all images into a single image
          mergeImages(imagePaths).then((mergedImagePath) => {
            try {
              const file = blobToFile(mergedImagePath);
              setValue("client_id.drivingLicense", file);
              setDrivingLicenseScanned(true);
              toast.success("Permisul de conducere a fost scanat cu succes!");
            } catch (error) {
              console.error("Error converting merged driving license image to file:", error);
              toast.error("Eroare la procesarea imaginii permisului.");
            }
          }).catch((error) => {
            console.error("Error merging driving license images:", error);
            toast.error("Eroare la unirea imaginilor permisului.");
          });
        } catch (error) {
          console.error("Error processing driving license images:", error);
          toast.error("Eroare la procesarea imaginilor permisului.");
        }
      } else {
        toast.error("Nu s-a putut extrage imaginea din scanare.");
      }
    } catch (error) {
      console.error("Error processing driving license:", error);
      toast.error("Eroare la procesarea permisului de conducere.");
      setScanDriverLicenseModal(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          p: { xs: 1, sm: 2, md: 3 },
          bgcolor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit, onFormError)}
          method="get"
          ref={ref}
          id={FormsIds.orderForm}
        >
          {/* Header */}
          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 2,
              bgcolor: "white",
              border: "1px solid #e2e8f0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    mb: 1,
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                  }}
                >
                  {Vocabulary.registerClient}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#64748b",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                >
                  Completează informațiile pentru înregistrarea unui nou client
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={() => setScanIdModal(true)}
                  size="large"
                  startIcon={<span style={{ fontSize: "1.5rem" }}>📷</span>}
                  sx={{
                    bgcolor: "#10b981",
                    color: "white",
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 14px 0 rgba(16, 185, 129, 0.25)",
                    whiteSpace: "nowrap",
                    minWidth: { xs: "auto", sm: "auto" },
                    "&:hover": {
                      bgcolor: "#059669",
                      boxShadow: "0 6px 20px 0 rgba(16, 185, 129, 0.35)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    Scanează buletinul
                  </Box>
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", sm: "none" } }}
                  >
                    Scanează CI
                  </Box>
                </Button>

                <Button
                  variant="contained"
                  onClick={() => setScanDriverLicenseModal(true)}
                  size="large"
                  startIcon={
                    drivingLicenseScanned ? (
                      <span style={{ fontSize: "1.5rem" }}>✅</span>
                    ) : (
                      <span style={{ fontSize: "1.5rem" }}>🚘</span>
                    )
                  }
                  sx={{
                    bgcolor: drivingLicenseScanned ? "#0284c7" : "#10b981",
                    color: "white",
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: drivingLicenseScanned
                      ? "0 4px 14px 0 rgba(2, 132, 199, 0.25) "
                      : "0 4px 14px 0 rgba(16, 185, 129, 0.25)",
                    whiteSpace: "nowrap",
                    minWidth: { xs: "auto", sm: "auto" },
                    "&:hover": {
                      bgcolor: drivingLicenseScanned ? "#0369a1" : "#059669",
                      boxShadow: drivingLicenseScanned
                        ? "0 6px 20px 0 rgba(2, 132, 199, 0.25) "
                        : "0 6px 20px 0 rgba(16, 185, 129, 0.35)",
                    },
                  }}
                >
                  {drivingLicenseScanned ? "Permis scanat" : "Scanează permis"}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Main Content Grid */}
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {/* Left Column - Service Selection */}
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 2, sm: 3, md: 4 },
                  borderRadius: 3,
                  height: "fit-content",
                  mb: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  🎓 Selectează serviciul
                </Typography>
                <ProductPanel
                  register={register}
                  reset={reset}
                  getValues={getValues}
                  setValue={setValue}
                  watch={watch}
                  control={control}
                  products={products}
                  managements={managements || []}
                  trigger={trigger}
                  clearErrors={clearErrors}
                  name="ProductPanel"
                />
              </Paper>

              {/* Recommendations Section */}
              <Paper
                elevation={2}
                sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  🎁 Recomandare{" "}
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    (opțional)
                  </Typography>
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name="recommendation_id.user_id"
                      control={control}
                      defaultValue={null}
                      render={({
                        field: { ref, ...field },
                      }) => (
                        <Autocomplete
                          options={users ?? []}
                          freeSolo={false}
                          disablePortal
                          clearOnBlur={true}
                          value={
                            users?.find((user) => user._id === field?.value) ||
                            null
                          }
                          noOptionsText={Vocabulary.noResultsFound}
                          isOptionEqualToValue={(option: any, value: any) => {
                            return option?._id === value?._id && value?._id;
                          }}
                          getOptionLabel={(option: any) =>
                            `${option?.firstName} ${option?.lastName}`
                          }
                          onChange={(event, value: any) => {
                            field.onChange(value?._id);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={Vocabulary?.recomendedBy}
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="recommendation_id.clientIdNumber"
                      control={control}
                      defaultValue={null}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.cnpRecommended}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="recommendation_id.recommendationValue"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type="number"
                          label={Vocabulary.reward}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {Vocabulary.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Right Column - Client Info & Address */}
            <Grid item xs={12} lg={8}>
              {/* Client Information */}
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 2, sm: 3, md: 4 },
                  mb: { xs: 2, sm: 3, md: 4 },
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  👤 Informații client
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="client_id.CNP"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.cnpClient}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="client_id.lastName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.name}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="client_id.firstName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.firstName}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="client_id.phone"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.phone}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalPhoneIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="client_id.email"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.email}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address Information */}
              <Paper
                elevation={2}
                sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  🏠 Informații adresă
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="client_id.address.country"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.country}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="client_id.address.county"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.county}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="client_id.address.city"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label={Vocabulary.city}
                          fullWidth
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              mt: { xs: 2, sm: 3, md: 4 },
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Button
                size="large"
                variant="outlined"
                color="error"
                onClick={() => navigate(localUrlEnum.orders)}
                sx={{
                  borderRadius: 3,
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  width: { xs: "100%", sm: "auto" },
                  order: { xs: 2, sm: 1 },
                }}
              >
                {Vocabulary.cancel}
              </Button>

              <Button
                type="submit"
                size="large"
                variant="contained"
                sx={{
                  borderRadius: 3,
                  px: { xs: 4, sm: 6 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 600,
                  width: { xs: "100%", sm: "auto" },
                  order: { xs: 1, sm: 2 },
                }}
              >
                {Vocabulary.saveClient}
              </Button>
            </Box>
          </Paper>
        </form>
      </Box>

      <ScanId
        openModal={scanIdModal}
        handleClose={() => setScanIdModal(false)}
        doneProcess={processCICompleted}
        documentType={ScanDocumentType.ID}
      />

      <ScanId
        openModal={scanDriverLicenseModal}
        handleClose={() => setScanDriverLicenseModal(false)}
        doneProcess={processDrivingLicenseCompleted}
        documentType={ScanDocumentType.DRIVER_LICENSE}
      />
    </>
  );
}

export default withRole(OrderForm);
